import React, { useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import DeleteAppointmentModal from "../modals/deleteAppointmentModal";
import { CalendarClock, Redo, Trash2Icon, UserRound } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { toast } from "sonner";
import Spinner1 from "../common/spinners";
import { useDashboard } from "../../context/dashboardContext";

const MyAppointmentTable = ({ data }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleReason, setRescheduleReason] = useState("");
  const itemsPerPage = 5;

  const {
    loading,
    handleFetAppointments,
    handleReScheculeAppointment,
    showDetailsModal,
    setShowDetailsModal,
    handleCancelAppointment,
  } = useDashboard();

  const filteredData = useMemo(
    () =>
      (Array.isArray(data) ? data : []).filter(
        (item) =>
          item?.whomToSee?.first_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.whomToSee?.profession?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.department?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
      ),
    [data, searchTerm]
  );

  const upcomingItems = filteredData.filter(
    (item) =>
      moment(item?.appointment_date).isAfter(moment()) &&
      item?.status !== "Cancelled" &&
      item?.status !== "Completed"
  );

  const totalPages = Math.max(1, Math.ceil(upcomingItems.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = upcomingItems.slice(indexOfFirstItem, indexOfLastItem);

  const openDeleteModal = (item) => {
    if (item?.transaction?.status !== 0) {
      setShowDeleteModal(true);
      setSelectedAppointment(item);
      return;
    }

    toast.error("You cannot cancel this appointment");
  };

  const openDetailsModal = (item) => {
    setSelectedAppointment(item);
    setShowDetailsModal(true);
  };

  const handleReschedule = () => {
    const payload = {
      appointment_date: rescheduleDate,
      id: selectedAppointment?.id,
      description: rescheduleReason,
      duration: rescheduleTime,
      duration_type: selectedAppointment?.duration_type,
    };

    handleReScheculeAppointment(payload).then(() => {
      toast.success("Appointment rescheduled successfully");
      handleFetAppointments();
    });
  };

  return (
    <>
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Upcoming appointments</h5>
          <p>Track what is next, then reschedule or cancel without digging through older records.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search doctor, profession, or department"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No upcoming appointments</h6>
            <p>When you book a visit, it will appear here with quick actions.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Doctor</th>
                  <th>Department</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <UserRound size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title text-capitalize">
                            {item?.whomToSee?.first_name} {item?.whomToSee?.last_name}
                          </div>
                          <div className="data-cell-meta">{item?.whomToSee?.profession || "Consultant"}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title">{item?.department?.name}</div>
                      <div className="data-cell-meta">{item?.whomToSee?.job_title || "Appointment care team"}</div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <CalendarClock size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title">
                            {moment(item?.appointment_date).format("ddd, Do MMM YYYY")}
                          </div>
                          <div className="data-cell-meta">
                            {moment(item?.appointment_date).format("h:mm A")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`data-status-badge ${
                        item?.status === "Approved" || item?.status === "Completed"
                          ? "is-success"
                          : item?.status === "Pending"
                          ? "is-warning"
                          : item?.status === "Cancelled" || item?.status === "Missed"
                          ? "is-danger"
                          : "is-warning"
                      }`}>
                        {item?.status || "Scheduled"}
                      </span>
                    </td>
                    <td>
                      <div className="data-actions">
                        <button
                          type="button"
                          className="data-action-btn is-danger"
                          onClick={() => openDeleteModal(item)}
                          title="Cancel appointment"
                        >
                          <Trash2Icon size={17} />
                        </button>
                        <button
                          type="button"
                          className="data-action-btn"
                          onClick={() => openDetailsModal(item)}
                          title="Reschedule appointment"
                        >
                          <Redo size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && upcomingItems.length > 0 && (
          <div className="data-pagination">
            <Button
              variant="light"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => page - 1)}
            >
              Previous
            </Button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reschedule Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAppointment && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>New Date</Form.Label>
                <DatePicker
                  selected={rescheduleDate}
                  onChange={(date) => setRescheduleDate(date)}
                  className="form-control"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>New Time</Form.Label>
                <Form.Control
                  type="time"
                  value={rescheduleTime}
                  onChange={(event) => setRescheduleTime(event.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Reason for Rescheduling</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={rescheduleReason}
                  onChange={(event) => setRescheduleReason(event.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleReschedule}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

      <DeleteAppointmentModal
        onConfirm={() => handleCancelAppointment(selectedAppointment?.id)}
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        data={selectedAppointment}
      />
    </>
  );
};

export default MyAppointmentTable;
